import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Modal } from 'react-bootstrap-5';

import DatePicker from 'react-datepicker';
import Emails from './emails';
import PhoneNumbers from './phone_numbers';
import Addresses from './addresses';
import { contactTypeOptions } from '../constants';
import { updateContact } from '../../store/actions/owners';
import { connect } from 'react-redux';

export function EditModal({ initialContact, show, onHide, updateContact }) {
  const [contact, setContact] = useState({
    firstName: initialContact.firstName,
    lastName: initialContact.lastName,
    jobTitle: initialContact.jobTitle,
    contactType: initialContact.contactType,
    id: initialContact.id,
    birthday: initialContact.birthday,
    myContact: true
  });

  const [emails, setEmails] = useState(initialContact.emails);
  const [phoneNumbers, setPhoneNumbers] = useState(initialContact.phoneNumbers);
  const [addresses, setAddresses] = useState(initialContact.addresses);

  useEffect(() => resetContact(), [initialContact]);

  const resetContact = () => {
    setContact(initialContact);
    setEmails(initialContact.emails);
    setPhoneNumbers(initialContact.phoneNumbers);
    setAddresses(initialContact.addresses);
  };

  const handleReset = () => {
    resetContact();
    onHide();
  };

  const handleFormChange = (e) => {
    setContact({ ...contact, [e.target.name]: e.target.value });
  };

  const handleSave = (e) => {
    e.stopPropagation();
    e.preventDefault();

    updateContact(initialContact.id, {
      ...contact,
      emailsAttributes: emails,
      phoneNumbersAttributes: phoneNumbers,
      addressesAttributes: addresses
    }).then(() => {
      onHide();
    });
  };

  const handleEmailChange = useCallback((idx, changes) => {
    setEmails(emails.map((email, index) => {
      if (index == idx) {
        return {
          ...email,
          ...changes
        };
      }

      return email;
    }));
  }, [emails]);

  const handleRemoveEmail = useCallback((idx) => {
    setEmails(emails.map((email, index) => {
      if (index == idx) {
        return {
          ...email,
          _destroy: true
        };
      }

      return email;
    }));
  }, [emails]);

  const handleNewEmail = useCallback(() => {
    setEmails([...emails, { }]);
  }, [emails]);

  const handlePhoneNumberChange = useCallback((idx, changes) => {
    setPhoneNumbers(phoneNumbers.map((phoneNumber, index) => {
      if (index == idx) {
        return {
          ...phoneNumber,
          ...changes
        };
      }

      return phoneNumber;
    }));
  }, [phoneNumbers]);

  const handleRemovePhoneNumber = useCallback((idx) => {
    setPhoneNumbers(phoneNumbers.map((phoneNumber, index) => {
      if (index == idx) {
        return {
          ...phoneNumber,
          _destroy: true
        };
      }

      return phoneNumber;
    }));
  }, [phoneNumbers]);

  const handleNewPhoneNumber = useCallback(() => {
    setPhoneNumbers([...phoneNumbers, { }]);
  }, [phoneNumbers]);

  const handleAddressChange = useCallback((idx, values) => {
    setAddresses(addresses.map((address, index) => {
      if (index == idx) {
        return {
          ...address,
          ...values
        };
      }

      return address;
    }));
  }, [addresses]);

  const handleRemoveAddress = useCallback((idx) => {
    setAddresses(addresses.map((address, index) => {
      if (index == idx) {
        return {
          ...address,
          _destroy: true
        };
      }

      return address;
    }));
  }, [addresses]);

  const handleNewAddress = useCallback(() => {
    setAddresses([...addresses, { }]);
  }, [addresses]);

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Contact</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>First Name</Form.Label>
            <Form.Control
              name="firstName"
              placeholder="First Name"
              required
              value={contact.firstName}
              onChange={handleFormChange}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              name="lastName"
              placeholder="Last Name"
              required
              value={contact.lastName}
              onChange={handleFormChange}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Job Title</Form.Label>
            <Form.Control
              name="jobTitle"
              placeholder="Job Title"
              value={contact.jobTitle || ''}
              onChange={handleFormChange}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Contact Type</Form.Label>
            <Form.Select
              name="contactType"
              placeholder="Contact Type"
              value={contact.contactType || ''}
              onChange={handleFormChange}
            >
              <option value="">Select Contact Type</option>
              {contactTypeOptions.map(option => (
                <option key={option.key} value={option.key}>{option.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Birthday</Form.Label>
            <div>
              <DatePicker
                className="form-control"
                selected={new Date(contact.birthday)}
                wrapperClassName="w-100"
                onChange={date => setContact({ ...contact, birthday: date })}
              />
            </div>
          </Form.Group>
          <Emails
            emails={emails}
            onChange={handleEmailChange}
            onNew={handleNewEmail}
            onRemove={handleRemoveEmail}
          />
          <PhoneNumbers
            phoneNumbers={phoneNumbers}
            onChange={handlePhoneNumberChange}
            onNew={handleNewPhoneNumber}
            onRemove={handleRemovePhoneNumber}
          />
          <Addresses
            addresses={addresses}
            onChange={handleAddressChange}
            onNew={handleNewAddress}
            onRemove={handleRemoveAddress}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={handleReset}>Cancel</Button>
          <Button type="submit" variant="secondary">Save</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

EditModal.propTypes = {
  initialContact: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    jobTitle: PropTypes.string,
    contactType: PropTypes.string,
    birthday: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ])
  }).isRequired,
  show: PropTypes.bool,
  updateContact: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired
};

export default connect(null, { updateContact })(EditModal);
